import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import Switch from 'react-switch'

import {
    getMessagingTopics,
    setMessagingTopicMode,
    updateMessagingTopic,
} from 'services/messagingTopics'
import { queueEvent } from 'utilities/analytics'
import { switchProps } from './switchProps'

export const TOPICS_KEY = ['messaging-topics']

const CHANNEL_LABEL: Record<TSampleChannel, string> = {
    bale: 'در بله',
    eitaa: 'در ایتا',
    sms: 'پیامک',
    push: 'اعلان گوشی',
}

/**
 * The message a switch sends, the way it reaches this member.
 *
 * بله shows its buttons under the bubble; ایتا writes them as link lines at
 * the end of the text; a پیامک or a push has neither.
 */
export function SampleMessage({ sample }: { sample: ITopicSample }) {
    const isBale = sample.channel === 'bale'
    const isEitaa = sample.channel === 'eitaa'
    return (
        <div
            className={`mt-2 rounded-lg p-2 ${
                isEitaa ? 'bg-[#ECE4D8]' : 'bg-[#D7E6DC]'
            }`}
            data-testid="topic-sample"
        >
            <p className="text-[11px] text-text-secondary mb-1">
                {sample.only_on_messenger
                    ? 'فقط در بله و ایتا فرستاده می‌شود'
                    : CHANNEL_LABEL[sample.channel]}
            </p>
            <div className="bg-white rounded-lg px-3 py-2 text-xs leading-6 whitespace-pre-wrap break-words">
                {sample.text}
                {isEitaa &&
                    sample.buttons.map((button) => (
                        <span
                            key={button}
                            className="block text-[#1B64C8] underline"
                        >
                            {button}
                        </span>
                    ))}
            </div>
            {isBale && sample.buttons.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                    {sample.buttons.map((button) => (
                        <span
                            key={button}
                            className="flex-1 min-w-[45%] text-center text-[11px] rounded-md bg-white/70 border border-divider px-2 py-1 truncate"
                        >
                            {button}
                        </span>
                    ))}
                </div>
            )}
        </div>
    )
}

const MODE_LABELS: Record<string, string> = {
    '': 'هر بار',
    daily: 'خلاصهٔ روزانه، ساعت ۲۱',
}

function TopicRow({
    topic,
    highlighted,
    onToggle,
    onMode,
}: {
    topic: ITopic
    highlighted: boolean
    onToggle: () => void
    onMode: (mode: string) => void
}) {
    // Open by itself when the member arrived from «🔕 خاموش کردن این اعلان»:
    // the whole point of the link is «this is the message you just got».
    const [open, setOpen] = useState(highlighted)
    useEffect(() => {
        if (highlighted) setOpen(true)
    }, [highlighted])
    return (
        <div
            id={`topic-${topic.key}`}
            className={`py-3 border-b border-divider last:border-b-0 ${
                highlighted ? 'bg-brand-soft rounded-md px-2 -mx-2' : ''
            }`}
        >
            {highlighted && (
                <p className="text-[11px] font-bold text-brand-deep mb-1">
                    از همان پیام آمده‌اید؛ این همان نوع اعلان است.
                </p>
            )}
            <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                    <p className="text-sm font-bold">{topic.title}</p>
                    <p className="text-xs text-text-secondary mt-1 leading-5">
                        {topic.hint}
                    </p>
                </div>
                {topic.locked ? (
                    <span className="text-[11px] text-text-secondary whitespace-nowrap">
                        🔒 همیشه روشن
                    </span>
                ) : (
                    <Switch
                        {...switchProps}
                        onChange={onToggle}
                        checked={topic.enabled}
                        aria-label={topic.title}
                    />
                )}
            </div>
            {topic.enabled && (topic.modes?.length ?? 0) > 1 && (
                <div
                    className="mt-2 flex gap-1 rounded-full bg-sunken p-1"
                    role="radiogroup"
                    aria-label={`نحوهٔ رسیدن ${topic.title}`}
                >
                    {topic.modes!.map((mode) => (
                        <button
                            key={mode || 'instant'}
                            type="button"
                            role="radio"
                            aria-checked={(topic.mode ?? '') === mode}
                            onClick={() =>
                                (topic.mode ?? '') !== mode && onMode(mode)
                            }
                            className={`flex-1 rounded-full px-2 py-1 text-[11px] ${
                                (topic.mode ?? '') === mode
                                    ? 'bg-white font-bold text-brand-deep shadow-sm'
                                    : 'text-text-secondary'
                            }`}
                        >
                            {MODE_LABELS[mode] ?? mode}
                        </button>
                    ))}
                </div>
            )}
            {topic.sample && (
                <>
                    <button
                        type="button"
                        className="mt-1 text-xs text-brand-deep"
                        onClick={() => setOpen((value) => !value)}
                        aria-expanded={open}
                    >
                        {open ? 'بستن نمونهٔ پیام' : 'نمونهٔ پیام'}
                    </button>
                    {open && <SampleMessage sample={topic.sample} />}
                </>
            )}
        </div>
    )
}

/**
 * «چه خبرهایی بفرستیم» — per fund, and inside a fund only the sections that
 * are yours: as its manager, as a guarantor, as a member (your loan, your
 * dues…). A message's «⚙️ مدیریت اعلان‌ها» opens this on its fund
 * (`?box=`); «🔕 خاموش کردن این اعلان» adds `&topic=` and lands on that one
 * switch, lit up, with its sample open.
 */
export default function TopicsSection() {
    const queryClient = useQueryClient()
    const [params] = useSearchParams()
    const wantedBox = params.get('box')
    const wantedTopic = params.get('topic')
    const source =
        params.get('from') ??
        (wantedTopic ? 'mute_link' : wantedBox ? 'message' : 'page')
    const [box, setBox] = useState<string | null>(wantedBox)
    const scrolled = useRef(false)
    const opened = useRef(false)

    const { data, isLoading, isError, refetch } = useQuery(
        TOPICS_KEY,
        getMessagingTopics,
        { staleTime: 60_000, retry: 1 }
    )

    const funds = data?.funds ?? []
    const fund = funds.find((item) => item.box === box) ?? funds[0] ?? null

    useEffect(() => {
        if (opened.current || isLoading) return
        opened.current = true
        queueEvent('notification_settings_opened', {
            source,
            box: wantedBox,
            topic: wantedTopic,
            funds: funds.length,
        })
        // Once, when the first answer is in.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading])

    useEffect(() => {
        if (scrolled.current || !wantedTopic || !fund) return
        const row = document.getElementById(`topic-${wantedTopic}`)
        if (!row) return
        scrolled.current = true
        row.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
    }, [fund, wantedTopic])

    const toggle = async (target: ITopicFund, topic: ITopic) => {
        const enabled = !topic.enabled
        const previous = data
        if (!previous) return
        const flip = (item: ITopicFund): ITopicFund =>
            item.box !== target.box
                ? item
                : {
                      ...item,
                      sections: item.sections.map((section) => ({
                          ...section,
                          groups: section.groups.map((group) => ({
                              ...group,
                              topics: group.topics.map((row) =>
                                  row.key === topic.key
                                      ? { ...row, enabled }
                                      : row
                              ),
                          })),
                      })),
                  }
        queryClient.setQueryData<ITopicsResponse>(TOPICS_KEY, {
            ...previous,
            funds: previous.funds.map(flip),
        })
        try {
            const updated = await updateMessagingTopic(
                target.box,
                topic.key,
                enabled
            )
            queryClient.setQueryData<ITopicsResponse>(
                TOPICS_KEY,
                (current) => ({
                    ...(current ?? previous),
                    funds: (current ?? previous).funds.map((item) =>
                        item.box === updated.box ? updated : item
                    ),
                })
            )
            queueEvent('notification_topic_toggled', {
                topic: topic.key,
                box_type: target.box_type,
                box_id: target.box_id,
                enabled,
                source,
            })
            toast.info(
                enabled
                    ? `«${topic.title}» در «${target.name}» روشن شد.`
                    : `«${topic.title}» در «${target.name}» خاموش شد؛ در «پیام‌ها»ی همیان می‌ماند.`
            )
        } catch {
            queryClient.setQueryData(TOPICS_KEY, previous)
            toast.error('تغییر ذخیره نشد. دوباره امتحان کنید.')
        }
    }

    const changeMode = async (
        target: ITopicFund,
        topic: ITopic,
        mode: string
    ) => {
        try {
            const updated = await setMessagingTopicMode(
                target.box,
                topic.key,
                mode
            )
            queryClient.setQueryData<ITopicsResponse>(
                TOPICS_KEY,
                (current) => ({
                    ...(current as ITopicsResponse),
                    funds: (current?.funds ?? []).map((item) =>
                        item.box === updated.box ? updated : item
                    ),
                })
            )
            queueEvent('notification_topic_mode_changed', {
                topic: topic.key,
                box_type: target.box_type,
                box_id: target.box_id,
                mode: mode || 'instant',
                source,
            })
            toast.info(
                mode === 'daily'
                    ? `«${topic.title}» هر شب ساعت ۲۱ یک‌جا می‌رسد.`
                    : `«${topic.title}» هر بار می‌رسد.`
            )
        } catch {
            toast.error('تغییر ذخیره نشد. دوباره امتحان کنید.')
        }
    }

    if (isError) {
        return (
            <section className="bg-white rounded-lg shadow-header p-4">
                <button
                    type="button"
                    onClick={() => refetch()}
                    className="text-xs text-brand-deep underline"
                >
                    فهرست اعلان‌ها بارگیری نشد؛ دوباره امتحان کنید
                </button>
            </section>
        )
    }
    if (isLoading || !fund) return null

    return (
        <section className="bg-white rounded-lg shadow-header p-4" id="topics">
            <p className="text-base font-bold">چه خبرهایی بفرستیم؟</p>
            <p className="text-xs text-text-secondary mt-1 leading-6">
                برای هر صندوق جدا. کنار هر نوع، نمونهٔ همان پیامی هست که
                می‌فرستیم.
            </p>

            {funds.length > 1 && (
                <div
                    className="mt-3 flex gap-2 overflow-x-auto pb-1"
                    role="tablist"
                >
                    {funds.map((item) => (
                        <button
                            key={item.box}
                            type="button"
                            role="tab"
                            aria-selected={item.box === fund.box}
                            onClick={() => setBox(item.box)}
                            className={`shrink-0 rounded-full border px-3 py-1 text-xs ${
                                item.box === fund.box
                                    ? 'bg-brand text-white border-brand'
                                    : 'border-divider'
                            }`}
                        >
                            {item.box_type === 'cashbox'
                                ? `${item.name} · قرعه‌کشی`
                                : item.name}
                        </button>
                    ))}
                </div>
            )}

            {fund.sections.map((section) => (
                <div
                    key={section.domain}
                    className="mt-4 rounded-lg border border-divider px-3 pb-1"
                >
                    <p className="text-sm font-bold pt-3">{section.title}</p>
                    {section.guarantees && section.guarantees.length > 0 && (
                        <p className="text-xs text-text-secondary mt-1 leading-5">
                            {section.guarantees.join('، ')}
                        </p>
                    )}
                    {section.groups.map((group) => (
                        <div key={group.title || section.domain}>
                            {group.title && (
                                <p className="text-[11px] font-bold text-text-secondary mt-3">
                                    {group.title}
                                </p>
                            )}
                            {group.topics.map((topic) => (
                                <TopicRow
                                    key={topic.key}
                                    topic={topic}
                                    highlighted={
                                        topic.key === wantedTopic &&
                                        (!wantedBox || wantedBox === fund.box)
                                    }
                                    onToggle={() => toggle(fund, topic)}
                                    onMode={(mode) =>
                                        changeMode(fund, topic, mode)
                                    }
                                />
                            ))}
                        </div>
                    ))}
                </div>
            ))}

            <p className="mt-3 text-xs text-text-secondary leading-6">
                خاموش یعنی این خبر در بله، ایتا، پیامک و اعلان گوشی نمی‌آید. در
                «پیام‌ها»ی همیان همیشه می‌ماند تا چیزی گم نشود.
            </p>
        </section>
    )
}
